import React, { createContext, useContext, useState, ReactNode } from 'react';
import axios from 'axios';

import { jwtAuthService } from '@services/public/admin/jwt-auth.service';
import { jwtReissueService } from '@services/public//admin/jwt-reissue.service';
import { isAdmin, isStaff } from '@utils/getRole';
import { clearStorage, getItem, setItem } from '@utils/localStorage';
import { is200xRange } from '@utils/utilities';
import { setLogoutFunction } from '@utils/axiosInstance';

interface AuthContextType {
  loading: boolean;
  isLoggedIn: boolean;
  login: () => void;
  logout: () => void;
  setIsLoggedIn: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const token = getItem('api_access_token');
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const verifyToken = async () => {
    const { response, statusCode, error } = await jwtAuthService();

    if (
      error ||
      !is200xRange(statusCode) ||
      (response?.code !== '15210' && response?.code !== '1')
    ) {
      setLoading(false);
    }

    if (response?.code === '15210') {
      const { response, statusCode } = await jwtReissueService();
      const code = response?.code;
      const jwtReissueInfo = response?.result?.jwtReissueInfo;

      if (is200xRange(statusCode)) {
        if (code === '1') {
          axios.defaults.headers.common['Authorization'] =
            `${jwtReissueInfo.grantType} ${jwtReissueInfo.accessToken}`;
          setItem('api_access_token', jwtReissueInfo.accessToken);
        }
        if (code !== '1') {
          clearStorage();
        }
      }
    }

    if (response?.code === '1' && is200xRange(statusCode)) {
      setLoading(false);
      setItem('isLoginInitiated', 'true');
      setIsLoggedIn(true);
    }
  };

  const login = () => {
    setLoading(true);
    if (isStaff && token) {
      // @ts-ignore
      setItem('isLoginInitiated', true);
    }
    if (isAdmin) {
      verifyToken();
    }
  };

  const logout = (errorCode?: any, msg?: any) => {
    clearStorage();
    setIsLoggedIn(false);
    if (errorCode) {
      window.location.href = `/?error=${errorCode}`;
    }
    else if (msg) {
      window.location.href = `/?msg=${msg}`;
    }
    else {
      window.location.href = '/';
    }
  };

  setLogoutFunction(logout); // set logout function to axios instance

  return (
    <AuthContext.Provider
      value={{
        loading,
        isLoggedIn,
        login,
        logout,
        setIsLoggedIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
